import * as React from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { cn } from "@/lib/utils";
import { MENU_CLASS } from "./row-menu";

export interface Crumb {
  readonly id: string;
  readonly label: string;
}

const ITEM =
  "flex w-full cursor-default select-none items-center rounded px-2 py-[5px] text-left text-body-s outline-none data-[highlighted]:bg-panel";

/**
 * A path «Форготтен реалмс › … › Склеп Тресендара › Камера Сильдара» (sidebar polish П9, crumbs of VS Code): every level
 * but the last is a button (it opens that level); when the path does not fit, the middle collapses into «…» with a menu of
 * the hidden levels. No «← Назад»: a step back is the previous level.
 */
export function Crumbs({
  items,
  onPick,
  hiddenLabel,
  className,
  testid,
  itemTestid,
  currentClassName,
}: {
  readonly items: readonly Crumb[];
  /** Absent — the path is words only (nothing to open). */
  readonly onPick?: (id: string) => void;
  readonly hiddenLabel: string;
  readonly className?: string;
  readonly testid?: string;
  /** testid of level i: `${itemTestid}-${i}`. */
  readonly itemTestid?: string;
  readonly currentClassName?: string;
}): React.JSX.Element {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [hide, setHide] = React.useState(0);
  const [width, setWidth] = React.useState(0);
  const key = items.map((i) => i.id).join("/");
  // A new path or a new width measures again from the whole path.
  React.useLayoutEffect(() => setHide(0), [key, width]);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (el !== null && el.scrollWidth > el.clientWidth + 1 && hide < items.length - 2) setHide((h) => h + 1);
  });
  React.useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => setWidth(Math.round(entries[0]?.contentRect.width ?? 0)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hidden = hide > 0 ? items.slice(1, 1 + hide) : [];
  const sep = (k: string): React.JSX.Element => (
    <span key={k} className="flex-none px-0.5 text-dim" aria-hidden="true">
      ›
    </span>
  );
  const level = (item: Crumb, i: number): React.JSX.Element => {
    const last = i === items.length - 1;
    if (last || onPick === undefined)
      return (
        <span key={item.id} className={cn("min-w-0 truncate", last && (currentClassName ?? "font-semibold"))} data-testid={itemTestid ? `${itemTestid}-${i}` : undefined} title={item.label}>
          {item.label}
        </span>
      );
    return (
      <button
        key={item.id}
        type="button"
        className="min-w-0 max-w-48 flex-none truncate rounded px-0.5 text-muted-foreground hover:bg-fg/6 hover:text-fg focus-visible:outline-2 focus-visible:outline-hl"
        data-testid={itemTestid ? `${itemTestid}-${i}` : undefined}
        title={item.label}
        onClick={() => onPick(item.id)}
      >
        {item.label}
      </button>
    );
  };

  const out: React.JSX.Element[] = [];
  items.forEach((item, i) => {
    if (i >= 1 && i <= hide) {
      if (i === 1) {
        out.push(sep(`s${i}`));
        out.push(
          <DropdownMenuPrimitive.Root key="dots" modal={false}>
            <DropdownMenuPrimitive.Trigger asChild>
              <button type="button" className="flex-none rounded px-1 text-muted-foreground hover:bg-fg/6 hover:text-fg" aria-label={hiddenLabel} title={hiddenLabel} data-testid="crumbs-hidden">
                …
              </button>
            </DropdownMenuPrimitive.Trigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.Content align="start" sideOffset={4} className={MENU_CLASS}>
                {hidden.map((h) => (
                  <DropdownMenuPrimitive.Item key={h.id} className={ITEM} disabled={onPick === undefined} onSelect={() => onPick?.(h.id)}>
                    {h.label}
                  </DropdownMenuPrimitive.Item>
                ))}
              </DropdownMenuPrimitive.Content>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Root>,
        );
      }
      return;
    }
    if (i > 0) out.push(sep(`s${i}`));
    out.push(level(item, i));
  });

  return (
    <span ref={ref} className={cn("flex min-w-0 items-center overflow-hidden whitespace-nowrap", className)} data-testid={testid}>
      {out}
    </span>
  );
}
